import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import api from '../api';
import LoadingSpinner from '../components/LoadingSpinner';
import BottomNav from '../components/BottomNav';
import Toast, { useToast } from '../components/Toast';

const STATUS_CONFIG = {
  pending:   { className: 'tag-order-pending',   label: '접수 대기' },
  accepted:  { className: 'tag-order-accepted',  label: '주문 접수' },
  cooking:   { className: 'tag-order-cooking',   label: '조리 중' },
  ready:     { className: 'tag-order-served',    label: '준비 완료' },
  served:    { className: 'tag-order-served',    label: '서빙 완료' },
  completed: { className: 'tag-order-served',    label: '완료' },
  cancelled: { className: 'tag-order-cancelled', label: '취소됨' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return <span className={`tag ${cfg.className}`}>{cfg.label}</span>;
}

const toUtcDate = (str) => {
  if (!str) return null;
  if (!str.includes('Z') && !str.includes('+')) return new Date(str + 'Z');
  return new Date(str);
};

function formatDate(str) {
  const d = toUtcDate(str);
  if (!d) return '';
  return d.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });
}

function formatTime(str) {
  const d = toUtcDate(str);
  if (!d) return '';
  return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
}

function OrderCard({ order, orderIndex, onReorder }) {
  const opts = (item) => (Array.isArray(item.options) ? item.options : []);
  const isCancelled = order.status === 'cancelled';
  const isCompleted =
    order.status === 'served' ||
    order.status === 'completed' ||
    order.status === 'ready';

  return (
    <div
      className="animate-fade-in-up"
      style={{
        background: 'var(--surface)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-sm)',
        padding: 20,
        opacity: isCancelled ? 0.6 : 1,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
          주문 #{orderIndex} &middot; {formatTime(order.created_at)}
        </div>
        <StatusBadge status={order.status} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(order.items || []).map((item, iIdx) => (
          <div key={iIdx} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 15 }}>
              <span style={{ color: 'var(--text)' }}>
                {item.menu_name || item.name}{' '}
                <span style={{ color: 'var(--text-tertiary)' }}>x{item.quantity}</span>
              </span>
              <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                {(item.subtotal || (item.unit_price || 0) * item.quantity).toLocaleString()}원
              </span>
            </div>
            {opts(item).length > 0 && (
              <div style={{ fontSize: 13, color: 'var(--text-tertiary)', paddingLeft: 4 }}>
                {opts(item)
                  .map((o) => `+ ${o.name}${(o.price_delta || 0) > 0 ? ` (+${o.price_delta.toLocaleString()}원)` : ''}`)
                  .join(' · ')}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="divider" style={{ margin: '12px 0 10px' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>소계</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
          {(order.total_amount || 0).toLocaleString()}원
        </span>
      </div>

      {!isCancelled && (
        <button
          className="btn btn-outline"
          style={{ marginTop: 12, width: '100%', fontSize: 13, padding: '10px 12px' }}
          onClick={() => onReorder(order)}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            style={{ marginRight: 4 }}
          >
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
          재주문
        </button>
      )}
    </div>
  );
}

function SessionGroup({ session, isCurrentSession, onReorder }) {
  const orders = session.orders || [];
  const totalAmount = session.total_amount || 0;
  const label = isCurrentSession ? '현재 방문' : formatDate(session.created_at);

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div className="section-title" style={{ margin: 0 }}>
          {label}
        </div>
        {isCurrentSession && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              background: 'var(--primary)',
              color: '#fff',
              borderRadius: 6,
              padding: '2px 7px',
            }}
          >
            현재
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {orders.map((order, idx) => (
          <OrderCard
            key={order.id || idx}
            order={order}
            orderIndex={idx + 1}
            onReorder={onReorder}
          />
        ))}
      </div>

      {orders.length > 1 && (
        <div
          style={{
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-md)',
            padding: '12px 16px',
            marginTop: 10,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 14, color: 'var(--text-secondary)', fontWeight: 600 }}>
            방문 합계
          </span>
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
            {totalAmount.toLocaleString()}원
          </span>
        </div>
      )}
    </div>
  );
}

export default function OrderHistoryPage() {
  const { storeId, tableId } = useParams();
  const navigate = useNavigate();
  const { sessionId, addItem } = useCart();
  const { toast, showToast } = useToast();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    if (!storeId || !tableId) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get(`/orders/history/${storeId}/${tableId}?limit=20`);
      const result = res?.data || res;
      setSessions(result?.sessions || []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [storeId, tableId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleReorder = (order) => {
    const items = order.items || [];
    items.forEach((item) => {
      addItem({
        menuId: item.menu_id || item.id,
        name: item.menu_name || item.name || '메뉴',
        price: item.unit_price || item.price || 0,
        quantity: item.quantity || 1,
        options: Array.isArray(item.options) ? item.options : [],
        imageUrl: item.image_url || item.imageUrl,
      });
    });
    showToast('장바구니에 담았습니다', 'success');
    if (storeId && tableId) {
      navigate(`/cart/${storeId}/${tableId}`);
    }
  };

  if (loading) return <LoadingSpinner fullPage />;

  return (
    <>
      <div className="page" style={{ paddingBottom: 'calc(60px + var(--safe-bottom))' }}>
        <header className="page-header">
          <div style={{ width: 36 }} />
          <span className="page-header-title">주문 이력</span>
          <button
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-tertiary)',
              padding: 4,
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={fetchHistory}
            title="새로고침"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          </button>
        </header>

        <div style={{ padding: '0 20px' }}>
          {sessions.length === 0 ? (
            <div className="empty-state" style={{ paddingTop: 80 }}>
              <div className="empty-state-icon">
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <circle cx="3" cy="6" r="1" fill="currentColor" />
                  <circle cx="3" cy="12" r="1" fill="currentColor" />
                  <circle cx="3" cy="18" r="1" fill="currentColor" />
                </svg>
              </div>
              <div className="empty-state-title">아직 주문 내역이 없습니다</div>
              <div
                style={{
                  fontSize: 14,
                  color: 'var(--text-quaternary)',
                  marginTop: 8,
                  textAlign: 'center',
                }}
              >
                메뉴를 선택하고 첫 주문을 해보세요
              </div>
              {storeId && tableId && (
                <button
                  className="btn btn-primary"
                  style={{ marginTop: 24 }}
                  onClick={() => navigate(`/table/${storeId}/${tableId}`)}
                >
                  메뉴 보러 가기
                </button>
              )}
            </div>
          ) : (
            <div style={{ paddingTop: 8 }}>
              {sessions.map((session, idx) => (
                <SessionGroup
                  key={session.id || idx}
                  session={session}
                  isCurrentSession={session.id === sessionId}
                  onReorder={handleReorder}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
      <Toast {...toast} />
    </>
  );
}
